import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BookOpen, CircleHelp, LogOut, ShoppingBag } from "lucide-react";
import Header from "../components/ui/Header";
import MenuButton from "../components/ui/MenuButton";
import Popup from "../components/ui/Popup";
import Button from "../components/ui/Button";
import LogoutModal from "../components/LogoutModal/LogoutModal";
import useCustomerMenu from "../hooks/useCustomerMenu";
import { withBookTag } from "../util/bookTag";
import { useBuilderNav } from "../components/builder/BuilderNavContext";

/**
 * The signed-in customer's header: the redesigned `Header` with the site
 * menu wired to the app (cart count, My Books rule, sign out).
 *
 * Etsy buyers get the logo only. Every menu item is a Shopify dead end for
 * them: My Books is a library with add-to-cart buttons, Finalize Order is a
 * checkout they already paid on Etsy, Sign Out leaves an account they don't
 * have. They may still get a Back button inside the builder.
 * Help opens the Help page (/help, designed 2026-10-23).
 */

/** The builder step before each wizard route, for the header's Back button. */
const BACK_FROM = {
  "/create": "/dashboard?stepCode=Upload_Your_Cover_Photo",
  "/cover-image": "/create?stepCode=Upload_Images",
  "/review": "/cover-image?stepCode=Design_Your_Cover_Page",
};

const CustomerHeader = ({ progress = null, back }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const builderNav = useBuilderNav();
  const menu = useCustomerMenu();
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [emptyCartOpen, setEmptyCartOpen] = useState(false);

  const params = new URLSearchParams(location.search);
  const tag = params.get("tag");
  let computedBack = back;
  if (computedBack === undefined) {
    const stepCode = params.get("stepCode");
    if (location.pathname === "/dashboard" && stepCode === "Upload_Your_Cover_Photo") {
      computedBack = { to: withBookTag("/create-book?stepCode=Create_New_Book", tag) };
    } else if (BACK_FROM[location.pathname]) {
      computedBack = { to: withBookTag(BACK_FROM[location.pathname], tag) };
    } else if (location.pathname === "/gift/plan" && params.get("id")) {
      // Gift 2 came from Gift 1 (or from the hub's "Change how your books are split").
      computedBack = { to: `/gift/welcome?id=${encodeURIComponent(params.get("id"))}` };
    } else if (location.pathname === "/finalize-order") {
      // Several books in the cart have no single preview to return to.
      computedBack = { to: menu.showMyBooks ? "/dashboard" : withBookTag("/review?stepCode=Review_Your_Book", tag) };
    } else {
      computedBack = null;
    }
  }

  // A builder step may register its own Back (it saves the step first); otherwise go to the computed route.
  if (computedBack?.to && !computedBack.onClick) {
    const to = computedBack.to;
    computedBack = {
      ...computedBack,
      // Leaving a step while its photos are still saving would cancel the uploads.
      disabled: !!builderNav?.busy,
      onClick: () => {
        const own = builderNav?.getBack?.();
        if (own && own() !== false) return;
        navigate(to);
      },
    };
  }

  if (menu.isEtsy) {
    return <Header back={computedBack} progress={progress} />;
  }

  const items = [
    ...(menu.showMyBooks
      ? [{ key: "books", label: "My Books", icon: <BookOpen className="h-5 w-5" />, onClick: menu.goToMyBooks }]
      : []),
    {
      key: "cart",
      label: "Finalize Order",
      icon: <ShoppingBag className="h-5 w-5" />,
      badge: menu.cartCount || null,
      onClick: () => {
        if (!menu.goToFinalizeOrder()) setEmptyCartOpen(true);
      },
    },
    { key: "help", label: "Help", icon: <CircleHelp className="h-5 w-5" />, tone: "muted", onClick: () => navigate("/help") },
    { divider: true },
    { key: "signout", label: "Sign Out", icon: <LogOut className="h-5 w-5" />, tone: "quiet", onClick: () => setLogoutOpen(true) },
  ];

  return (
    <>
      <Header
        back={computedBack}
        progress={progress}
        logoHref={process.env.REACT_APP_SHOPIFY_URL}
        menu={menu.isAuthenticated ? <MenuButton items={items} signedInAs={menu.email} /> : null}
      />
      <LogoutModal isOpen={logoutOpen} setIsOpen={setLogoutOpen} />
      <Popup
        open={emptyCartOpen}
        onClose={() => setEmptyCartOpen(false)}
        icon={<ShoppingBag className="h-7 w-7" />}
        title="Your order is empty"
        actions={[
          <Button key="ok" onClick={() => setEmptyCartOpen(false)}>
            Got it
          </Button>,
        ]}
      >
        Finish a book and add it to your order first, or start a new one.
      </Popup>
    </>
  );
};

export default CustomerHeader;
